import { defineStore } from 'pinia';
import { api, ApiError } from '@/api/client';
import type { TimerSession, TimerStopResponse } from '@/api/types';

interface TimerState {
  active: TimerSession | null;
  // Local clock-tick to drive the live mm:ss counter without re-fetching.
  tickAt: number;  // ms since epoch from a setInterval(1000) callback
  /** Mutex so the FAB and TimerBlock can't both fire auto-stop. */
  _autoStopping: boolean;
}

export const useTimerStore = defineStore('timer', {
  state: (): TimerState => ({ active: null, tickAt: Date.now(), _autoStopping: false }),
  getters: {
    isRunning: (s) => s.active?.status === 'running',
    isPaused:  (s) => s.active?.status === 'paused',
    /** Live elapsed seconds for the active session, including time since the last server-known accumulation. */
    elapsedSeconds(): number {
      if (!this.active) return 0;
      const base = this.active.accumulatedSeconds;
      if (this.active.status !== 'running') return base;
      const since = (this.tickAt - new Date(this.active.lastHeartbeatAt).getTime()) / 1000;
      return Math.max(0, base + Math.min(since, this.active.heartbeatIntervalSeconds + 300));
    },
    /** Seconds until the next heartbeat is due, computed locally to avoid a server roundtrip per tick. */
    secondsUntilHeartbeat(): number {
      if (!this.active || this.active.status !== 'running') return 0;
      const dueAt = new Date(this.active.lastHeartbeatAt).getTime() + this.active.heartbeatIntervalSeconds * 1000;
      return Math.max(0, Math.round((dueAt - this.tickAt) / 1000));
    },
    /**
     * Seconds remaining until the planned session end. Negative when
     * the user has run past the planned end (overrun). Null when the
     * event has no parseable duration (then UIs should fall back to
     * counting up).
     */
    remainingSeconds(): number | null {
      if (!this.active) return null;
      const planned = this.active.plannedDurationSeconds;
      if (planned == null || planned <= 0) return null;
      return Math.round(planned - this.elapsedSeconds);
    },
  },
  actions: {
    bumpTick() { this.tickAt = Date.now(); },
    async fetchActive() {
      // Endpoint returns 200 + null when no active timer.
      try {
        const resp = await api<TimerSession | null>('GET', '/api/timer/active');
        this.active = resp ?? null;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) this.active = null;
        else throw e;
      }
    },
    async start(eventId: string) {
      this.active = await api<TimerSession>('POST', '/api/timer/start', { eventId });
    },
    async heartbeat() {
      if (!this.active || this.active.status !== 'running') return;
      this.active = await api<TimerSession>('POST', '/api/timer/heartbeat');
    },
    async pause() {
      this.active = await api<TimerSession>('POST', '/api/timer/pause');
    },
    async resume() {
      this.active = await api<TimerSession>('POST', '/api/timer/resume');
    },
    async stop(): Promise<TimerStopResponse> {
      const resp = await api<TimerStopResponse>('POST', '/api/timer/stop');
      this.active = null;
      return resp;
    },
    /**
     * Auto-stop when the planned end is reached, but only for sessions
     * shorter than the heartbeat interval — long sessions intentionally
     * keep running so users can earn Marathon-Maker. Idempotent and
     * mutex'd so multiple watchers (TimerBlock + TimerFab) calling on
     * the same tick fire stop() exactly once.
     */
    async maybeAutoStop(): Promise<TimerStopResponse | null> {
      if (this._autoStopping) return null;
      if (!this.active || this.active.status !== 'running') return null;
      const planned = this.active.plannedDurationSeconds;
      if (planned == null || planned <= 0) return null;
      const heartbeat = this.active.heartbeatIntervalSeconds || 1800;
      if (planned >= heartbeat) return null;          // long session — let it overrun
      if (this.elapsedSeconds < planned) return null; // not at zero yet
      this._autoStopping = true;
      try {
        return await this.stop();
      } catch {
        return null;
      } finally {
        this._autoStopping = false;
      }
    },
  },
});
